import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Dropdown,
  DropdownTriggerButton,
  DropdownContent,
  DropDownSelectToClose,
} from '@/components/Dropdown';
import { ErrorBanner } from '@/components/ErrorBanner';
import { FileUploadField } from './FileUploadField';
import { VersionNameField } from './VersionNameField';
import { currentDateTimePart } from './versionName';
import { useFirmwareMutations, useFirmwareFile, useFirmwareVersionEditor } from '../../hooks';
import type { IdNameType, Country } from '../../types';
import { FormField } from '@/components';
import { FormFieldAny } from '@/components/FormField';

interface FirmwareCreateViewProps {
  modelId: number;
  firmwareTypes: IdNameType[];
  countries: Country[];
  initialTypeId?: number;
  initialCountryId?: string;
  onCreated: () => void;
}

export function FirmwareCreateView({
  modelId,
  firmwareTypes,
  countries,
  initialTypeId,
  initialCountryId,
  onCreated,
}: FirmwareCreateViewProps) {
  const { t } = useTranslation();

  const editor = useFirmwareVersionEditor({
    modelId,
    initialFirmwareTypeId: initialTypeId ?? null,
    initialCountryId: initialCountryId ?? null,
    initialNumberPart: '',
    initialDateTimePart: currentDateTimePart(),
    initialFirmwareName: '',
    initialSavedVersion: '',
  });

  // A file is required so the version number can always be derived from it; selecting one feeds
  // the derived number and name into the editor, clearing it resets them.
  const fileUpload = useFirmwareFile({
    modelId,
    onFileDerived: editor.applyUploadedFile,
    onFileCleared: () => editor.applyUploadedFile('', ''),
  });

  const { create } = useFirmwareMutations();
  const [createError, setCreateError] = useState<string | null>(null);

  const isCreating = create.isPending || fileUpload.isUploading;

  const selectedType = firmwareTypes.find((ft) => ft.id === editor.firmwareTypeId);
  const selectedCountry = countries.find((c) => c.countryID === editor.countryId);

  const isInputValid = editor.firmwareTypeId !== null &&
    fileUpload.file !== null &&
    editor.isVersionComplete &&
    editor.versionStatus === 'available';

  // Creates the firmware record, then uploads the selected file to it. The panel closes on
  // success; an upload failure still closes it since the firmware itself was created.
  const handleCreate = useCallback(async () => {
    if (!isInputValid || editor.firmwareTypeId === null) return;
    setCreateError(null);

    try {
      const result = await create.mutateAsync({
        modelId,
        firmwareTypeId: editor.firmwareTypeId,
        countryId: editor.countryId,
        versionName: editor.versionName.trim(),
        firmwareName: editor.firmwareName.trim() || undefined,
      });
      try {
        await fileUpload.uploadTo(result.id);
        toast.success(t('firmware.sidePanel.uploadSuccess'));
      } catch {
        toast.error(t('firmware.sidePanel.uploadError'));
      }
      onCreated();
    } catch {
      setCreateError(t('firmware.sidePanel.createError'));
    }
  }, [isInputValid, editor, modelId, create, fileUpload, onCreated, t]);

  const canCreate = isInputValid && !isCreating;

  return (
    <>
      <FormFieldAny
        labelKey="firmware.sidePanel.type"
        label=''
      >
        <Dropdown>
          <DropdownTriggerButton className="w-full justify-between">
            <span className="truncate">
              {selectedType?.name || t('editableDropdown.select')}
            </span>
            <svg className="shrink-0 size-3.5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
          </DropdownTriggerButton>
          <DropdownContent>
            <div className="p-1">
              {firmwareTypes.map((ft) => (
                <DropDownSelectToClose key={ft.id} doSelect={() => editor.setFirmwareTypeId(ft.id)}>
                  <button
                    type="button"
                    className={`w-full flex items-center gap-x-3 py-1.5 px-2 rounded-lg text-[13px] font-normal hover:bg-surface-darker ${editor.firmwareTypeId === ft.id ? 'text-primary' : 'text-onsurface'}`}
                  >
                    {ft.name}
                  </button>
                </DropDownSelectToClose>
              ))}
            </div>
          </DropdownContent>
        </Dropdown>
      </FormFieldAny>

      <FormFieldAny
        labelKey="firmware.sidePanel.country"
        label=''
      >
        <Dropdown>
          <DropdownTriggerButton className="w-full justify-between">
            <span className="truncate">
              {editor.countryId === null ? t('firmware.filters.global') : selectedCountry?.countryID || t('editableDropdown.select')}
            </span>
            <svg className="shrink-0 size-3.5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
          </DropdownTriggerButton>
          <DropdownContent>
            <div className="p-1">
              <DropDownSelectToClose key="__global__" doSelect={() => editor.setCountryId(null)}>
                <button
                  type="button"
                  className={`w-full flex items-center gap-x-3 py-1.5 px-2 rounded-lg text-[13px] font-normal hover:bg-surface-darker ${editor.countryId === null ? 'text-primary' : 'text-onsurface'}`}
                >
                  {t('firmware.filters.global')}
                </button>
              </DropDownSelectToClose>
              {countries.map((c) => (
                <DropDownSelectToClose key={c.countryID} doSelect={() => editor.setCountryId(c.countryID)}>
                  <button
                    type="button"
                    className={`w-full flex items-center gap-x-3 py-1.5 px-2 rounded-lg text-[13px] font-normal hover:bg-surface-darker ${editor.countryId === c.countryID ? 'text-primary' : 'text-onsurface'}`}
                  >
                    {c.countryID}
                  </button>
                </DropDownSelectToClose>
              ))}
            </div>
          </DropdownContent>
        </Dropdown>
      </FormFieldAny>

      <FormFieldAny
        labelKey="firmware.sidePanel.file"
        label=''
      >
        <FileUploadField file={fileUpload.file} onFileSelect={fileUpload.selectFile} />
      </FormFieldAny>



      <VersionNameField
        numberPart={editor.numberPart}
        dateTimePart={editor.dateTimePart}
        onNumberChange={editor.setNumberPart}
        onDateTimeChange={editor.setDateTimePart}
        allowForceNumberEdit
        status={editor.versionStatus}
      />

      <FormField
        labelKey="firmware.sidePanel.firmwareName"
        label={t('firmware.sidePanel.firmwareName')}
        name="firmwareName"
        type="text"
        placeholder={t('firmware.sidePanel.firmwareNamePlaceholder')}
        value={editor.firmwareName}
        onChange={(e) => editor.setFirmwareName(e.target.value)}
        className="w-full"
      />


      <ErrorBanner hasError={!!createError}>
        {createError}
      </ErrorBanner>

      <div className="pt-0">
        <button
          type="button"
          onClick={handleCreate}
          disabled={!canCreate}
          className="w-full py-2 px-4 text-sm font-medium rounded-lg bg-primary text-onprimary shadow-2xs hover:bg-primary-darker disabled:opacity-50 disabled:pointer-events-none focus:outline-hidden focus:ring-2 focus:ring-primary"
        >
          {isCreating ? t('firmware.sidePanel.saving') : t('firmware.sidePanel.createButton')}
        </button>
      </div>
    </>
  );
}
